import { useNavigate, useParams, useSearchParams } from '@ksyos/router';
import { sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import type { CarePathActivityConditionRestriction } from '@common/enums';
import type { IServiceActivity } from '@common/interfaces';
import type { ISelectOption } from '@frontend/components/fields/Dropdown';
import type { IService } from '@frontend/components/interfaces';
import { CarePathActivityConditionField, ManagerAuthorization, ServiceCode } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { serviceDeclarationActivityAddEditSchema } from '@common/validation/serviceDeclarationActivity';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, emptyValidationErrors, updateValidationErrorsForOne, validate } from '@frontend/utils';
import type { DeclarationActivity } from './declarationActivity';
import {
    CancelButton,
    Dropdown,
    FormContainer,
    FormGroup,
    FormHeader,
    FormLabel,
    Header,
    InfoTooltip,
    InputCheckbox,
    InputGroup,
    InputText,
    Loading,
    NullableInputText,
    SaveSubmit,
} from '../../elements/common';
import { CarePathActivityCondition } from './CarePathActivityCondition';

export function DeclarationActivityAddEdit() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const serviceId = searchParams.getOptional('serviceId');
    const [errors, setErrors] = useState(emptyValidationErrors);
    const [isSaving, setIsSaving] = useState(false);
    const [service, setService] = useState<IService>();
    const [activityTypes, setActivityTypes] = useState<ISelectOption<string>[]>([]);
    const [declarationActivity, setDeclarationActivity] = useState<DeclarationActivity>();

    const translate = getSelectedLanguage();

    const canActivityHaveConditionChecks = {
        isOsasc2Report: () =>
            service?.code === ServiceCode.OSASC2 && declarationActivity?.activityType === 'respondingConsultation',
        isOsasvcRequest: () => service?.code === ServiceCode.OSASVC && declarationActivity?.activityType === 'request',
        isTpoProcedure: () => service?.code === ServiceCode.TPO && declarationActivity?.activityType === 'biometrics',
    };

    assertHasAuthorization(ManagerAuthorization.serviceAddEdit);

    useEffect(() => {
        loadService();

        if (id) {
            loadDeclarationActivity();
        } else {
            setDeclarationActivity({
                activityType: null,
                appliesWhenCustomControlDataQuery: null,
                codeActiviteit: '',
                codeAfsluitreden: null,
                codeDiagnose: null,
                codeSpecialisme: null,
                codeZorgtype: null,
                conditionField: null,
                conditionRestriction: null,
                defaultIcd10Code: null,
                id: '',
                isHonorariumDeclareren: false,
                isKostendeelDeclareren: false,
                serviceId,
            });
        }
    }, [id]);

    useEffect(() => {
        if (declarationActivity && service && canActivityHaveCondition() === false) {
            setDeclarationActivity({
                ...declarationActivity,
                conditionField: null,
                conditionRestriction: null,
            } as DeclarationActivity);
        }
    }, [declarationActivity?.activityType]);

    useEffect(() => {
        loadActivityTypeOptions();
    }, [serviceId]);

    return (
        <PageTemplate width="medium">
            <Header
                content={formatString(id ? translate.common.editItem : translate.common.addItem, [
                    translate.serviceDeclarationRules.declarationActivity,
                ])}
            />

            <Loading
                item={declarationActivity}
                loaded={(declarationActivityToDisplay) => (
                    <FormContainer onSubmit={save}>
                        <FormHeader>{translate.serviceDeclarationRules.declarationActivities}</FormHeader>

                        <FormGroup>
                            <InputGroup>
                                <InputCheckbox
                                    checked={declarationActivityToDisplay.isHonorariumDeclareren}
                                    error={errors.get('isHonorariumDeclareren')}
                                    onChange={(value) => handleChange('isHonorariumDeclareren', value)}
                                    text={translate.serviceDeclarationActivities.isHonorariumDeclareren}
                                />
                            </InputGroup>

                            <InputGroup>
                                <InputCheckbox
                                    checked={declarationActivityToDisplay.isKostendeelDeclareren}
                                    error={errors.get('isKostendeelDeclareren')}
                                    onChange={(value) => handleChange('isKostendeelDeclareren', value)}
                                    text={translate.serviceDeclarationActivities.isKostendeelDeclareren}
                                />
                            </InputGroup>
                        </FormGroup>

                        <FormHeader>{translate.common.settings}</FormHeader>

                        <FormGroup hasErrors={errors.has('activityType')}>
                            <FormLabel isRequired={true}>{translate.common.activity}</FormLabel>

                            <InputGroup>
                                <Dropdown
                                    error={errors.get('activityType')}
                                    onChange={(value) => handleChange('activityType', value)}
                                    onDoValidate={(value) => validateOne('activityType', value)}
                                    placeholder={translate.common.activity}
                                    selectOptions={activityTypes}
                                    value={declarationActivityToDisplay.activityType}
                                />
                            </InputGroup>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>{translate.serviceDeclarationActivities.codeSpecialisme}</FormLabel>

                            <InputGroup>
                                <NullableInputText
                                    error={errors.get('codeSpecialisme')}
                                    onChange={(value) => handleChange('codeSpecialisme', value)}
                                    onDoValidate={(value) => validateOne('codeSpecialisme', value ?? null)}
                                    placeholder={translate.serviceDeclarationActivities.codeSpecialisme}
                                    value={declarationActivityToDisplay.codeSpecialisme}
                                />
                            </InputGroup>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>{translate.serviceDeclarationActivities.codeDiagnose}</FormLabel>

                            <InputGroup>
                                <NullableInputText
                                    error={errors.get('codeDiagnose')}
                                    onChange={(value) => handleChange('codeDiagnose', value)}
                                    onDoValidate={(value) => validateOne('codeDiagnose', value ?? null)}
                                    placeholder={translate.serviceDeclarationActivities.codeDiagnose}
                                    value={declarationActivityToDisplay.codeDiagnose}
                                />
                            </InputGroup>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel isRequired={true}>
                                {translate.serviceDeclarationActivities.codeActiviteit}
                            </FormLabel>

                            <InputGroup>
                                <InputText
                                    error={errors.get('codeActiviteit')}
                                    onChange={(value) => handleChange('codeActiviteit', value)}
                                    onDoValidate={(value) => validateOne('codeActiviteit', value ?? null)}
                                    placeholder={translate.serviceDeclarationActivities.codeActiviteit}
                                    value={declarationActivityToDisplay.codeActiviteit}
                                />
                            </InputGroup>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>{translate.serviceDeclarationActivities.codeZorgtype}</FormLabel>

                            <InputGroup>
                                <NullableInputText
                                    error={errors.get('codeZorgtype')}
                                    onChange={(value) => handleChange('codeZorgtype', value)}
                                    onDoValidate={(value) => validateOne('codeZorgtype', value ?? null)}
                                    placeholder={translate.serviceDeclarationActivities.codeZorgtype}
                                    value={declarationActivityToDisplay.codeZorgtype}
                                />
                            </InputGroup>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>{translate.serviceDeclarationActivities.codeAfsluitreden}</FormLabel>

                            <InputGroup>
                                <NullableInputText
                                    error={errors.get('codeAfsluitreden')}
                                    onChange={(value) => handleChange('codeAfsluitreden', value)}
                                    onDoValidate={(value) => validateOne('codeAfsluitreden', value ?? null)}
                                    placeholder={translate.serviceDeclarationActivities.codeAfsluitreden}
                                    value={declarationActivityToDisplay.codeAfsluitreden}
                                />
                            </InputGroup>
                        </FormGroup>

                        <FormGroup>
                            <FormLabel>{translate.serviceDeclarationActivities.defaultIcd10}</FormLabel>

                            <InputGroup>
                                <NullableInputText
                                    error={errors.get('defaultIcd10Code')}
                                    onChange={(value) => handleChange('defaultIcd10Code', value)}
                                    onDoValidate={(value) => validateOne('defaultIcd10Code', value ?? null)}
                                    placeholder={translate.serviceDeclarationActivities.defaultIcd10}
                                    value={declarationActivityToDisplay.defaultIcd10Code}
                                />

                                <InfoTooltip text={translate.serviceDeclarationActivities.icd10Text} />
                            </InputGroup>
                        </FormGroup>

                        <FormHeader>{translate.common.conditions}</FormHeader>

                        <FormGroup>
                            <FormLabel>
                                {translate.serviceDeclarationActivities.appliesWhenCustomControlDataQueryLabel}
                            </FormLabel>

                            <InputGroup>
                                <NullableInputText
                                    error={errors.get('appliesWhenCustomControlDataQuery')}
                                    onChange={(value) => handleChange('appliesWhenCustomControlDataQuery', value)}
                                    onDoValidate={(value) =>
                                        validateOne('appliesWhenCustomControlDataQuery', value ?? null)
                                    }
                                    placeholder={
                                        translate.serviceDeclarationActivities.appliesWhenCustomControlDataQuery
                                    }
                                    value={declarationActivityToDisplay.appliesWhenCustomControlDataQuery}
                                />
                                <InfoTooltip text={translate.serviceDeclarationActivities.queryText} />
                            </InputGroup>
                        </FormGroup>

                        {canActivityHaveConditionChecks.isTpoProcedure() && (
                            <CarePathActivityCondition
                                label={translate.serviceDeclarationActivities.spirogramIncludingReversibilityTest}
                                onChange={(value) =>
                                    handleConditionChange(CarePathActivityConditionField.isReversibilityTest, value)
                                }
                                value={declarationActivityToDisplay.conditionRestriction}
                            />
                        )}

                        {(canActivityHaveConditionChecks.isOsasc2Report() ||
                            canActivityHaveConditionChecks.isOsasvcRequest()) && (
                            <CarePathActivityCondition
                                label={translate.serviceDeclarationActivities.cpapTreatment}
                                onChange={(value) =>
                                    handleConditionChange(CarePathActivityConditionField.isCpap, value)
                                }
                                value={declarationActivityToDisplay.conditionRestriction}
                            />
                        )}

                        <CancelButton
                            disabled={isSaving}
                            to={`/services/${
                                serviceId || declarationActivityToDisplay.serviceId
                            }?tab=declarationActivities`}
                        />

                        <SaveSubmit disabled={isSaving} />
                    </FormContainer>
                )}
            />
        </PageTemplate>
    );

    function canActivityHaveCondition() {
        return Object.values(canActivityHaveConditionChecks).some((conditionCheck) => conditionCheck());
    }

    function handleChange<K extends keyof DeclarationActivity>(name: K, value: DeclarationActivity[K]) {
        setDeclarationActivity({ ...declarationActivity, [name]: value } as DeclarationActivity);
    }

    function handleConditionChange(
        field: CarePathActivityConditionField,
        value: CarePathActivityConditionRestriction | null
    ) {
        setDeclarationActivity({
            ...declarationActivity,
            conditionField: value ? field : null,
            conditionRestriction: value,
        } as DeclarationActivity);
    }

    function validateOne<K extends keyof DeclarationActivity>(name: K, value: DeclarationActivity[K] | null) {
        const updatedDeclarationActivity = { ...declarationActivity, [name]: value };

        const validationErrors = updateValidationErrorsForOne(
            serviceDeclarationActivityAddEditSchema,
            updatedDeclarationActivity,
            errors,
            name
        );

        setErrors(validationErrors);
    }

    async function loadDeclarationActivity() {
        if (!id) {
            return;
        }

        const url = `/api/serviceDeclarationActivities/${id}`;
        const result = await fetchJson(url, 'GET');

        if (result.json) {
            setDeclarationActivity(result.json);
        }
    }

    async function loadService() {
        if (!serviceId) {
            return;
        }

        const url = `/api/services/${serviceId}`;
        const result = await fetchJson(url, 'GET');

        if (result.json) {
            setService(result.json);
        }
    }

    async function loadActivityTypeOptions() {
        const url = `/api/services/${serviceId}/serviceActivities`;
        const result = await fetchJson(url, 'GET');

        if (result.json?.items?.length > 0) {
            // Flex
            setActivityTypes(
                sortBy(
                    result.json.items.map(({ customControlCode, name }: IServiceActivity) => ({
                        text: name,
                        value: customControlCode,
                    })),
                    (item) => item.text
                )
            );
        } else {
            // Oldschool
            setActivityTypes([
                {
                    text: translate.activityTypeEnum.request,
                    value: 'request',
                },
                {
                    text: translate.activityTypeEnum.biometrics,
                    value: 'biometrics',
                },
                {
                    text: translate.activityTypeEnum.grading,
                    value: 'grading',
                },
                {
                    text: translate.activityTypeEnum.respondingConsultation,
                    value: 'respondingConsultation',
                },
            ]);
        }
    }

    async function save() {
        const { errors: validatedErrors, item } = validate(
            serviceDeclarationActivityAddEditSchema,
            declarationActivity
        );

        setErrors(validatedErrors);

        if (validatedErrors.size > 0) {
            return;
        }

        setIsSaving(true);

        const url = `/api/serviceDeclarationActivities${id ? `/${id}` : ''}`;
        await fetchJson(url, 'POST', item);

        navigate(`/services/${item!.serviceId}?tab=declarationActivities`);
    }
}
